import React, { useState, useEffect } from "react";
import { Button, Input, AutoComplete, Spin, Switch } from "antd";
import {
  UserOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  MailOutlined,
  PhoneOutlined,
  SafetyOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import toast from "react-hot-toast";
import {
  getCompany,
  getCustomersInt,
  createOTP,
  validateOTP,
} from "../../Store/Apis/ReservationApi";
import "./CustomerSelection.css";
import { FaRegBuilding } from "react-icons/fa";

const CustomerSelection = ({ onCustomerSelected, bookingType }) => {
  const [step, setStep] = useState("selection"); // selection, search, otp, verified
  const [isExistingCustomer, setIsExistingCustomer] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchOptions, setSearchOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpLoading, setOtpLoading] = useState(false);
  const [verifiedCustomer, setVerifiedCustomer] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Use bookingType prop instead of customerType state
  const customerType = bookingType === "individual" ? "individual" : "company";

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Handle existing customer toggle
  const handleExistingCustomerToggle = (checked) => {
    setIsExistingCustomer(checked);
    if (checked) {
      // Existing customer - go to search step
      setStep("search");
    } else {
      // New customer - proceed directly to booking
      onCustomerSelected({
        isExisting: false,
        customerType,
        customerData: null,
      });
    }
  };

  // Search for customers/companies with debouncing
  const handleSearch = async (value) => {
    setSearchValue(value);

    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // If search value is empty or too short, clear options
    if (!value || value.trim().length < 1) {
      setSearchOptions([]);
      return;
    }

    // Debounce the search to avoid too many API calls
    const timeoutId = setTimeout(async () => {
      setLoading(true);
      try {
        let results = [];

        if (customerType === "individual") {
          // Search customers
          const customers = await getCustomersInt({ keyword: value.trim() });
          console.log("Customer search results:", customers); // Debug log

          if (customers && Array.isArray(customers)) {
            results = customers
              .filter((customer) => customer) // Remove null/undefined entries
              .map((customer, index) => {
                // More flexible field mapping
                const name =
                  customer.customerName ||
                  customer.name ||
                  customer.fullName ||
                  `Customer ${index + 1}`;
                const email =
                  customer.email ||
                  customer.customerEmail ||
                  customer.emailAddress ||
                  "";
                // const phone = customer.phone || customer.phoneNumber || customer.contactNumber || '';

                return {
                  value: `${name}${email ? ` (${email})` : ""}`,
                  label: (
                    <div
                      className="search-option"
                      key={customer.customerID || customer.id || index}
                    >
                      <div className="search-option-icon">
                        <UserOutlined />
                      </div>
                      <div className="search-option-content">
                        <div className="search-option-name">{name}</div>
                        <div className="search-option-detail">
                          {email ? `${email} ` : email || "No contact info"}
                        </div>
                      </div>
                    </div>
                  ),
                  customer,
                  key:
                    customer.customerID || customer.id || `customer-${index}`,
                };
              });
          }
        } else {
          // Search companies
          const companies = await getCompany({
            companyName: value.trim(),
            outletID: "4",
          });
          console.log("Company search results:", companies); // Debug log

          if (companies && Array.isArray(companies)) {
            results = companies
              .filter((company) => company) // Remove null/undefined entries
              .map((company, index) => {
                // More flexible field mapping
                const name =
                  company.companyName || company.name || `Company ${index + 1}`;
                const email =
                  company.companyEmailAddress ||
                  company.email ||
                  company.emailAddress ||
                  "";
                // const phone = company.companyPhone || company.phone || company.phoneNumber || '';

                return {
                  value: `${name}${email ? ` (${email})` : ""}`,
                  label: (
                    <div
                      className="search-option"
                      key={company.companyID || company.id || index}
                    >
                      <div className="search-option-icon">
                        <FaRegBuilding />
                      </div>
                      <div className="search-option-content">
                        <div className="search-option-name">{name}</div>
                        <div className="search-option-detail">
                          {(email && `${email} `) || "No contact info"}
                        </div>
                      </div>
                    </div>
                  ),
                  company,
                  key: company.companyID || company.id || `company-${index}`,
                };
              });
          }
        }

        console.log("Processed search results:", results); // Debug log
        setSearchOptions(results);

        // If no results but API returned data, log for debugging
        if (results.length === 0 && value.trim().length > 0) {
          console.warn("No search results processed for:", value);
        }
      } catch (error) {
        console.error("Search error:", error);
        toast.error("Failed to search. Please try again.");
        setSearchOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300); // 300ms debounce delay

    setSearchTimeout(timeoutId);
  };

  // Handle customer selection
  const handleCustomerSelect = async (value, option) => {
    try {
      console.log("Selected option:", option); // Debug log

      if (!option || (!option.customer && !option.company)) {
        toast.error("Invalid selection. Please try again.");
        return;
      }

      const customerData =
        customerType === "individual" ? option.customer : option.company;

      if (!customerData) {
        toast.error("Customer data not found. Please try again.");
        return;
      }

      console.log("Selected customer data:", customerData); // Debug log
      setSelectedCustomer(customerData);
      setStep("otp");

      // Send OTP
      setOtpLoading(true);

      let otpData;
      if (customerType === "individual") {
        const customerId = customerData.customerID || customerData.id;
        if (!customerId) {
          throw new Error("Customer ID not found");
        }
        otpData = {
          customerID: customerId,
          companyID: 0,
        };
      } else {
        // For companies
        const companyId = customerData.companyID || customerData.id;
        if (!companyId) {
          throw new Error("Company ID not found");
        }
        otpData = {
          customerID: 0,
          companyID: companyId,
        };
      }

      console.log("Sending OTP with data:", otpData); // Debug log
      const result = await createOTP(otpData);
      if (result) {
        toast.success("OTP sent successfully!");
      } else {
        throw new Error("Failed to send OTP - no result returned");
      }
    } catch (error) {
      console.error("OTP send error:", error);
      toast.error(
        `Failed to send OTP: ${error.message || "Please try again."}`
      );
      // Reset to search step on OTP send failure
      setStep("search");
      setSelectedCustomer(null);
    } finally {
      setOtpLoading(false);
    }
  };

  // Handle OTP input
  const handleOtpChange = (index, value) => {
    if (value.length > 1) return; // Only allow single digit

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  // Handle OTP backspace
  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  // Verify OTP
  const handleOtpVerification = async () => {
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      toast.error("Please enter all 6 digits of the OTP");
      return;
    }

    setOtpLoading(true);
    try {
      const validation = await validateOTP(otpString);
      console.log("OTP validation response:", validation);

      if (validation.isValid) {
        // Use the validated customer data from the API response

        //check  if customerDetails or companyDetails exist
        if (validation.customerDetails) {
          const validatedCustomerData = validation.customerDetails;
          setSelectedCustomer(validatedCustomerData);

          setVerifiedCustomer({
            ...validatedCustomerData,
            customerID: validation.customerID,
            companyID: validation.companyID,
            customerType,
          });
          setStep("verified");

          // Pass verified customer data to parent using the validated data
          onCustomerSelected({
            isExisting: true,
            customerType,
            customerData: {
              ...validatedCustomerData,
              customerID: validation.customerID,
              companyID: validation.companyID,
            },
          });
        } else if (validation.companydetails) {
          const validatedCompanyData = validation.companydetails;
          setSelectedCustomer(validatedCompanyData);
          setVerifiedCustomer({
            ...validatedCompanyData,
            customerID: validation.customerID,
            companyID: validation.companyID,
            customerType,
          });
          setStep("verified");
          // Pass verified company data to parent using the validated data
          onCustomerSelected({
            isExisting: true,
            customerType,
            customerData: {
              ...validatedCompanyData,
              customerID: validation.customerID,
              companyID: validation.companyID,
            },
          });
        }
      } else {
        toast.error("Invalid OTP. Please try again.");
      }
    } catch (error) {
      console.error("OTP verification error:", error);
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <div className="customer-selection-container">
      {step === "selection" && (
        <div className="customer-selection-step">
          <div className="customer-selection-header">
            <h2 className="customer-selection-title">
              {customerType === "individual" ? "Individual" : "Company"}{" "}
              Customer Information
            </h2>
            <p className="customer-selection-subtitle">
              Are you an existing{" "}
              {customerType === "individual" ? "customer" : "client"} or booking
              for the first time?
            </p>
          </div>

          <div className="customer-type-display">
            <div className={`customer-type-display-card selected`}>
              <div className="customer-type-icon">
                {customerType === "individual" ? (
                  <UserOutlined />
                ) : (
                  <FaRegBuilding />
                )}
              </div>
              <h3>
                {customerType === "individual" ? "Individual" : "Company"}
              </h3>
              <p>
                {customerType === "individual"
                  ? "Personal booking"
                  : "Corporate booking"}
              </p>
            </div>
          </div>

          <div className="existing-customer-section">
            <div className="existing-customer-toggle">
              <span>
                I am an existing{" "}
                {customerType === "individual" ? "customer" : "client"}
              </span>
              <Switch
                checked={isExistingCustomer}
                onChange={handleExistingCustomerToggle}
                className="existing-customer-switch"
              />
            </div>

            {!isExistingCustomer && (
              <div className="new-customer-note">
                <div className="note-content">
                  <CheckCircleOutlined className="note-icon" />
                  <span>
                    You'll be able to create your profile during the booking
                    process
                  </span>
                </div>
              </div>
            )}

            {/* Continue button for new customers */}
            {!isExistingCustomer && (
              <div className="continue-new-customer">
                <Button
                  type="primary"
                  size="large"
                  onClick={() =>
                    onCustomerSelected({
                      isExisting: false,
                      customerType,
                      customerData: null,
                    })
                  }
                  className="continue-new-customer-button"
                >
                  Continue as New{" "}
                  {customerType === "individual" ? "Customer" : "Company"}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {step === "search" && (
        <div className="customer-search-step">
          <div className="customer-search-header">
            <h2 className="customer-search-title">
              Find Your {customerType === "individual" ? "Profile" : "Company"}
            </h2>
            <p className="customer-search-subtitle">
              Start typing to search for your existing{" "}
              {customerType === "individual" ? "profile" : "company"}
            </p>
          </div>

          <div className="search-input-container">
            <AutoComplete
              className="customer-search-input"
              options={searchOptions}
              onSearch={handleSearch}
              onSelect={handleCustomerSelect}
              placeholder={`Search for ${
                customerType === "individual"
                  ? "your name or email"
                  : "company name"
              }`}
              notFoundContent={
                loading ? (
                  <Spin size="small" />
                ) : searchValue && searchValue.length > 0 ? (
                  "No results found"
                ) : (
                  "Start typing to search..."
                )
              }
              size="large"
              suffixIcon={<SearchOutlined />}
              filterOption={false} // Disable client-side filtering to show all API results
              allowClear={true} // Allow users to clear the input easily
              showSearch={true} // Enable search functionality
              defaultActiveFirstOption={false} // Don't auto-select first option
              backfill={false} // Don't auto-fill the input on arrow navigation
            />

            <div className="search-helper-text">
              <div className="search-tips">
                {customerType === "individual"
                  ? "💡 Search tips: Try your full name, email address, or phone number"
                  : "💡 Search tips: Try your company name or company email address"}
              </div>
              {searchOptions.length > 0 && (
                <div className="search-results-info">
                  Found {searchOptions.length} result
                  {searchOptions.length !== 1 ? "s" : ""} for "{searchValue}"
                </div>
              )}
              {searchValue &&
                searchValue.length > 0 &&
                searchOptions.length === 0 &&
                !loading && (
                  <div className="no-results-info">
                    No results found for "{searchValue}". Try a different search
                    term or check your spelling.
                  </div>
                )}
            </div>
          </div>

          <div className="search-actions">
            <Button
              type="text"
              onClick={() => setStep("selection")}
              className="back-button"
            >
              ← Back to selection
            </Button>
          </div>
        </div>
      )}

      {step === "otp" && (
        <div className="otp-verification-step">
          <div className="otp-header">
            <div className="otp-icon">
              <SafetyOutlined />
            </div>
            <h2 className="otp-title">Verify Your Identity</h2>
            <p className="otp-subtitle">
              We've sent a 6-digit verification code to{" "}
              {selectedCustomer?.email || selectedCustomer?.companyEmailAddress}
            </p>
          </div>

          <div className="customer-preview">
            <div className="customer-preview-icon">
              {customerType === "individual" ? (
                <UserOutlined />
              ) : (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z" />
                </svg>
              )}
            </div>
            <div className="customer-preview-content">
              <div className="customer-preview-name">
                {selectedCustomer?.customerName ||
                  selectedCustomer?.name ||
                  selectedCustomer?.companyName}
              </div>
              <div className="customer-preview-email">
                {selectedCustomer?.email ||
                  selectedCustomer?.customerEmail ||
                  selectedCustomer?.companyEmailAddress}
              </div>
            </div>
          </div>

          <div className="otp-input-container">
            <div className="otp-inputs">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-input-${index}`}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  className="otp-digit-input"
                  disabled={otpLoading}
                />
              ))}
            </div>

            <Button
              type="primary"
              size="large"
              loading={otpLoading}
              onClick={handleOtpVerification}
              disabled={otp.join("").length !== 6}
              className="verify-otp-button"
              icon={<ArrowRightOutlined />}
            >
              Verify Code
            </Button>
          </div>

          <div className="otp-actions">
            <Button
              type="text"
              onClick={() => setStep("search")}
              disabled={otpLoading}
              className="back-button"
            >
              ← Back to search
            </Button>

            <Button type="text" disabled={otpLoading} className="resend-button">
              Resend Code
            </Button>
          </div>
        </div>
      )}

      {step === "verified" && (
        <div className="verification-success-step">
          <div className="success-icon">
            <CheckCircleOutlined />
          </div>
          <h2 className="success-title">Identity Verified!</h2>
          <p className="success-subtitle">
            Welcome back,{" "}
            {verifiedCustomer?.customerName ||
              verifiedCustomer?.name ||
              verifiedCustomer?.companyName}
          </p>

          <div className="verified-customer-card">
            <div className="verified-customer-icon">
              {customerType === "individual" ? (
                <UserOutlined />
              ) : (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z" />
                </svg>
              )}
            </div>
            <div className="verified-customer-details">
              <div className="verified-customer-name">
                {verifiedCustomer?.customerName ||
                  verifiedCustomer?.name ||
                  verifiedCustomer?.companyName}
              </div>
              <div className="verified-customer-email">
                {verifiedCustomer?.email ||
                  verifiedCustomer?.customerEmail ||
                  verifiedCustomer?.companyEmailAddress}
              </div>
              {verifiedCustomer?.phone && (
                <div className="verified-customer-phone">
                  <PhoneOutlined /> {verifiedCustomer.phone}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSelection;
